import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface EmailContextType {
  canEmail: boolean;
  loadingId: string | null;
  showContactModal: boolean;
  openContactModal: () => void;
  closeContactModal: () => void;
  handleQuoteRequest: (serviceType?: string, buttonId?: string) => void;
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export function EmailProvider({ children }: { children: ReactNode }) {
  const [canEmail, setCanEmail] = useState(true);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    // Check if mailto: links are likely to work
    // This is a heuristic - we check if we're in a context where mailto might not work
    const checkEmailCapability = () => {
      // Check if we're in an iframe or restricted context
      if (window.self !== window.top) {
        setCanEmail(false);
        return;
      }

      // Check for common indicators that mailto won't work
      // (e.g., some embedded browsers, kiosk modes)
      const userAgent = navigator.userAgent.toLowerCase();
      const isRestricted =
        userAgent.includes('electron') ||
        userAgent.includes('webview') ||
        // Add more checks as needed
        false;

      setCanEmail(!isRestricted);
    };

    checkEmailCapability();
  }, []);

  const openContactModal = () => setShowContactModal(true);
  const closeContactModal = () => setShowContactModal(false);

  const handleQuoteRequest = (serviceType?: string, buttonId?: string) => {
    const subject = encodeURIComponent(serviceType ? `Quote Request - ${serviceType}` : 'Quote Request');
    const body = encodeURIComponent(`Dear Dr. Sólyom,

I am writing to request a quote for your analytical services.

Sample/Material to be analyzed:
[Please describe what you need analyzed]

Timeline/Urgency:
[Please indicate your time constraints or desired turnaround]

Additional details:
[Any other relevant information]

Thank you,
[Your name]`);
    const mailtoUrl = `mailto:customerservice@gaasanalytical.com?subject=${subject}&body=${body}`;

    // Show loading state for specific button
    const id = buttonId || 'default';
    setLoadingId(id);

    // Track if we've left the page (mailto worked)
    let mailtoWorked = false;

    const handleBlur = () => {
      mailtoWorked = true;
      setLoadingId(null);
    };

    window.addEventListener('blur', handleBlur);

    // Try to open mailto link
    const link = document.createElement('a');
    link.href = mailtoUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Check after a short delay if mailto worked
    setTimeout(() => {
      window.removeEventListener('blur', handleBlur);
      setLoadingId(null);
      if (!mailtoWorked) {
        // mailto didn't work, show modal instead
        openContactModal();
      }
    }, 500);
  };

  return (
    <EmailContext.Provider
      value={{
        canEmail,
        loadingId,
        showContactModal,
        openContactModal,
        closeContactModal,
        handleQuoteRequest,
      }}
    >
      {children}
    </EmailContext.Provider>
  );
}

export function useEmail() {
  const context = useContext(EmailContext);
  if (context === undefined) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
}
